import React, { Component, createRef } from "react";

// CSS
import "./Courses.scss";

// Helper
import { checkCookie } from "../../Helper/Cookies";
import { apiFetch, getCurrentUser, uploadCourseMedia, UploadCancelledError } from "../../Helper/Api";

// Components
import Header from "../../components/Header/Header";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";

// Properties
const properties = require("../../properties.json");

const ITEM_TYPES = [
  { label: "فيديو", value: "VIDEO" },
  { label: "صوت", value: "AUDIO" },
  { label: "صورة", value: "IMAGE" },
  { label: "مذكرة / مستند", value: "DOCUMENT" },
  { label: "نموذج 3D (Rhino)", value: "MODEL_3D" },
];

const EMPTY_ITEM_FORM = { title: "", description: "", itemType: "VIDEO", contentUrl: "", fileName: "", thumb: "", isFreePreview: false, downloadAllowed: false };

// Used by the bulk multi-file uploader to auto-pick an item type per file instead of making the
// teacher set one dropdown per file -- matches CourseItemType on the backend.
const EXTENSION_ITEM_TYPE = {
  mp4: "VIDEO", mov: "VIDEO", avi: "VIDEO", webm: "VIDEO", mkv: "VIDEO", m4v: "VIDEO",
  mp3: "AUDIO", wav: "AUDIO", m4a: "AUDIO", aac: "AUDIO", ogg: "AUDIO", wma: "AUDIO",
  jpg: "IMAGE", jpeg: "IMAGE", png: "IMAGE", gif: "IMAGE", webp: "IMAGE", bmp: "IMAGE",
  "3dm": "MODEL_3D",
};

function detectItemType(fileName) {
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  return EXTENSION_ITEM_TYPE[ext] || "DOCUMENT";
}

function stripExtension(fileName) {
  const idx = fileName.lastIndexOf(".");
  return idx > 0 ? fileName.slice(0, idx) : fileName;
}

class CourseBuilder extends Component {
  constructor(props) {
    super(props);
    this.toast = createRef(null);
    // Unique per-file key for bulkUploads, so two files sharing a name in the same drop (or two
    // overlapping batches) get their own progress row instead of clobbering each other's state.
    this.bulkUploadSeq = 0;
  }

  state = {
    id: this.props.match && this.props.match.params ? this.props.match.params.id : null,

    course: {
      title: "",
      description: "",
      image: "",
      listImage: "",
      branchId: getCurrentUser()?.branchId,
      materialId: null,
      periodId: null,
      sortOrder: 0,
      showOnHome: false,
    },
    sections: [],

    materials: [],
    periods: [],
    allCourses: [], // this teacher's other courses, for the "move item" section/course picker

    newSectionTitle: "",
    itemForms: {}, // sectionId -> form state
    bulkUploads: {}, // sectionId -> { fileName -> progress percent } for the multi-file uploader
    editingSectionId: null,
    editingSectionTitle: "",
    movingItem: null, // { item, sectionId, targetCourseId, targetSectionId } while the move UI is open
    uploadingThumb: false,
    uploadingThumbProgress: 0,
    uploadingListImage: false,
    uploadingListImageProgress: 0,
    saving: false,
  };

  componentDidMount() {
    document.title = properties.site_name + " | " + (this.state.id ? "تعديل الدورة" : "دورة جديدة");

    if (!checkCookie("name")) {
      window.localStorage.removeItem("currentTeacher");
      window.location.href = `/`;
    }

    this.loadMaterials();
    if (this.state.id) {
      this.loadCourse();
      this.loadAllCourses();
    }
  }

  /** Every course this teacher owns, each with its own sections -- lets an item be moved to a
   *  different section without leaving this page, including a section under a different course. */
  loadAllCourses = () => {
    apiFetch("/courses")
      .then((allCourses) => this.setState({ allCourses }))
      .catch((error) => console.error("Error:", error));
  };

  loadMaterials = () => {
    const user = getCurrentUser();
    if (!user) return;
    apiFetch("/lookup/materials", { query: { branchId: user.branchId, teacherUserId: user.userId } })
      .then((materials) => this.setState({ materials }))
      .catch((error) => console.error("Error:", error));
  };

  loadPeriods = (materialId) => {
    if (!materialId) {
      this.setState({ periods: [] });
      return;
    }
    apiFetch("/periods/by-material", { query: { materialId } })
      .then((periods) => this.setState({ periods }))
      .catch((error) => console.error("Error:", error));
  };

  loadCourse = () => {
    apiFetch(`/courses/${this.state.id}`)
      .then((data) => {
        this.setState({
          course: {
            title: data.title,
            description: data.description,
            image: data.image,
            listImage: data.listImage,
            branchId: data.branchId,
            materialId: data.materialId,
            periodId: data.periodId,
            sortOrder: data.sortOrder,
            showOnHome: !!data.showOnHome,
          },
          sections: data.sections || [],
        });
        this.loadPeriods(data.materialId);
      })
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: "تعذر تحميل الدورة" });
      });
  };

  saveCourse = (e) => {
    e.preventDefault();
    const { course, id } = this.state;
    if (!course.title || !course.branchId || !course.periodId) {
      this.toast.current.show({ severity: "error", summary: "تنبيه", detail: "من فضلك أدخل إسم الدورة وإختر الفترة" });
      return;
    }
    this.setState({ saving: true });
    const request = apiFetch(id ? `/courses/${id}` : "/courses", { method: id ? "PUT" : "POST", body: course });
    request
      .then((saved) => {
        this.setState({ saving: false });
        this.toast.current.show({ severity: "success", summary: "تم بنجاح", detail: "تم حفظ الدورة بنجاح" });
        if (!id) {
          window.location.href = `/courses/edit/${saved.id}`;
        } else {
          this.loadCourse();
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ saving: false });
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  /** Refreshes both this course's own detail AND the full teacher's-courses list (the latter
   *  feeds the "move item" section/course picker, which needs to stay in sync with whatever just
   *  changed -- a newly added section, a renamed one, etc). */
  refreshCourse = () => {
    this.loadCourse();
    this.loadAllCourses();
  };

  uploadCourseThumb = async (event) => {
    const file = event.files && event.files[0];
    if (!file) return;
    this.setState({ uploadingThumb: true, uploadingThumbProgress: 0, uploadingThumbXhr: null });
    try {
      const url = await uploadCourseMedia(
        file,
        (pct) => this.setState({ uploadingThumbProgress: pct }),
        (xhr) => this.setState({ uploadingThumbXhr: xhr })
      );
      this.setState({ course: { ...this.state.course, image: url }, uploadingThumb: false, uploadingThumbXhr: null });
      this.toast.current.show({ severity: "info", summary: "تم بنجاح", detail: "تم رفع صورة الدورة" });
    } catch (error) {
      this.setState({ uploadingThumb: false, uploadingThumbXhr: null });
      if (error instanceof UploadCancelledError) return;
      console.error("Error:", error);
      this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "فشل رفع الصورة" });
    } finally {
      event.options && event.options.clear && event.options.clear();
    }
  };

  cancelThumbUpload = () => {
    this.state.uploadingThumbXhr && this.state.uploadingThumbXhr.abort();
  };

  // Separate from image/uploadCourseThumb above -- image is the home page + course detail cover,
  // this is what shows on the /courses listing card (see Courses.js on the public frontend).
  uploadCourseListImage = async (event) => {
    const file = event.files && event.files[0];
    if (!file) return;
    this.setState({ uploadingListImage: true, uploadingListImageProgress: 0, uploadingListImageXhr: null });
    try {
      const url = await uploadCourseMedia(
        file,
        (pct) => this.setState({ uploadingListImageProgress: pct }),
        (xhr) => this.setState({ uploadingListImageXhr: xhr })
      );
      this.setState({ course: { ...this.state.course, listImage: url }, uploadingListImage: false, uploadingListImageXhr: null });
      this.toast.current.show({ severity: "info", summary: "تم بنجاح", detail: "تم رفع صورة صفحة الدورات" });
    } catch (error) {
      this.setState({ uploadingListImage: false, uploadingListImageXhr: null });
      if (error instanceof UploadCancelledError) return;
      console.error("Error:", error);
      this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "فشل رفع الصورة" });
    } finally {
      event.options && event.options.clear && event.options.clear();
    }
  };

  cancelListImageUpload = () => {
    this.state.uploadingListImageXhr && this.state.uploadingListImageXhr.abort();
  };

  addSection = () => {
    if (!this.state.newSectionTitle) return;
    apiFetch(`/courses/${this.state.id}/sections`, {
      method: "POST",
      body: { title: this.state.newSectionTitle, sortOrder: this.state.sections.length },
    })
      .then(() => {
        this.setState({ newSectionTitle: "" });
        this.refreshCourse();
      })
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  deleteSection = (sectionId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا القسم وكل محتواه؟")) return;
    apiFetch(`/courses/${this.state.id}/sections/${sectionId}`, { method: "DELETE" })
      .then(() => this.refreshCourse())
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  // Renumbers every section to its new list position (0..n-1) rather than just swapping the two
  // moved sections' existing sortOrder values -- some courses (migrated data in particular) have
  // sections that already share a sortOrder, so swapping two equal values was a silent no-op: the
  // PUTs succeeded but nothing visibly moved. Rewriting the whole list's order on every move also
  // self-heals any such duplicates going forward.
  moveSection = (section, direction) => {
    const { sections } = this.state;
    const index = sections.findIndex((s) => s.id === section.id);
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= sections.length) return;
    const reordered = [...sections];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];
    Promise.all(
      reordered.map((s, i) =>
        apiFetch(`/courses/${this.state.id}/sections/${s.id}`, { method: "PUT", body: { title: s.title, sortOrder: i } })
      )
    )
      .then(() => this.refreshCourse())
      .catch((error) => console.error("Error:", error));
  };

  startEditSection = (section) => {
    this.setState({ editingSectionId: section.id, editingSectionTitle: section.title });
  };

  cancelEditSection = () => {
    this.setState({ editingSectionId: null, editingSectionTitle: "" });
  };

  saveEditSectionTitle = (section) => {
    const title = this.state.editingSectionTitle.trim();
    if (!title) return;
    apiFetch(`/courses/${this.state.id}/sections/${section.id}`, { method: "PUT", body: { title, sortOrder: section.sortOrder } })
      .then(() => {
        this.setState({ editingSectionId: null, editingSectionTitle: "" });
        this.refreshCourse();
      })
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  updateItemForm = (sectionId, patch) => {
    this.setState({
      itemForms: {
        ...this.state.itemForms,
        [sectionId]: { ...(this.state.itemForms[sectionId] || EMPTY_ITEM_FORM), ...patch },
      },
    });
  };

  uploadItemFile = async (sectionId, event) => {
    const file = event.files && event.files[0];
    if (!file) return;
    this.updateItemForm(sectionId, { uploading: true, uploadProgress: 0, uploadXhr: null });
    try {
      const url = await uploadCourseMedia(
        file,
        (pct) => this.updateItemForm(sectionId, { uploadProgress: pct }),
        (xhr) => this.updateItemForm(sectionId, { uploadXhr: xhr })
      );
      this.updateItemForm(sectionId, { contentUrl: url, fileName: file.name, uploading: false, uploadXhr: null });
      this.toast.current.show({ severity: "info", summary: "تم بنجاح", detail: "تم رفع الملف" });
    } catch (error) {
      this.updateItemForm(sectionId, { uploading: false, uploadXhr: null });
      if (error instanceof UploadCancelledError) return;
      console.error("Error:", error);
      this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "فشل رفع الملف" });
    } finally {
      event.options && event.options.clear && event.options.clear();
    }
  };

  cancelItemUpload = (sectionId) => {
    const form = this.state.itemForms[sectionId];
    form && form.uploadXhr && form.uploadXhr.abort();
  };

  removeUploadedItemFile = (sectionId) => {
    this.updateItemForm(sectionId, { contentUrl: "", fileName: "" });
  };

  addItem = (section) => {
    const form = this.state.itemForms[section.id] || EMPTY_ITEM_FORM;
    if (!form.title || !form.contentUrl) {
      this.toast.current.show({ severity: "error", summary: "تنبيه", detail: "من فضلك أدخل العنوان وارفع الملف" });
      return;
    }
    apiFetch(`/courses/${this.state.id}/sections/${section.id}/items`, {
      method: "POST",
      body: {
        title: form.title,
        description: form.description,
        itemType: form.itemType,
        contentUrl: form.contentUrl,
        thumb: form.thumb,
        isFreePreview: !!form.isFreePreview,
        downloadAllowed: !!form.downloadAllowed,
        sortOrder: (section.items || []).length,
      },
    })
      .then(() => {
        this.updateItemForm(section.id, EMPTY_ITEM_FORM);
        this.refreshCourse();
      })
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  /** Per-section queue (one promise chain per sectionId) that the "create item" call after each
   *  bulk-upload file goes through -- see uploadBulkFiles below for why. Lives outside state since
   *  it's pure plumbing, not something a render should ever read. */
  bulkFinalizeQueues = {};

  enqueueBulkFinalize = (sectionId, task) => {
    const previous = this.bulkFinalizeQueues[sectionId] || Promise.resolve();
    // Chained on both branches so one file's failed finalize never wedges the rest of the queue.
    const next = previous.then(task, task);
    this.bulkFinalizeQueues[sectionId] = next;
    return next;
  };

  updateBulkUpload = (sectionId, uploadId, patch) => {
    this.setState((s) => {
      const sectionUploads = s.bulkUploads[sectionId] || {};
      const current = sectionUploads[uploadId];
      if (patch === null) {
        const remaining = { ...sectionUploads };
        delete remaining[uploadId];
        return { bulkUploads: { ...s.bulkUploads, [sectionId]: remaining } };
      }
      return {
        bulkUploads: {
          ...s.bulkUploads,
          [sectionId]: { ...sectionUploads, [uploadId]: { ...(current || { progress: 0, xhr: null }), ...patch } },
        },
      };
    });
  };

  /** Bulk add: every dropped file becomes its own item, titled after its filename and typed by
   *  its extension (see detectItemType) -- for when a teacher has a folder of lesson files ready
   *  to go rather than adding them one at a time with a title/type per file.
   *
   *  The file transfers themselves run in parallel (each file gets its own progress row, updating
   *  independently, instead of the teacher watching one bar at a time while the rest sit at 0%).
   *  But the "create item" call that finalizes each upload is serialized per section through
   *  enqueueBulkFinalize -- manasa-service shouldn't see two of those hit the same course at once
   *  (each one reads the section's current item count to pick its sortOrder, so two racing writes
   *  could both land on the same value). sortOrder itself is precomputed once, up front, from this
   *  batch's starting item count + the file's index -- reading it fresh per-file from state would
   *  reintroduce the same race, since refreshCourse()'s re-fetch after each finalize won't have
   *  landed yet by the time the next one in the queue runs. */
  uploadBulkFiles = (section, event) => {
    const files = event.files || [];
    if (!files.length) return;
    const sectionId = section.id;
    const baseSortOrder = (section.items || []).length;

    files.forEach((file, index) => {
      const uploadId = ++this.bulkUploadSeq;
      this.updateBulkUpload(sectionId, uploadId, { progress: 0, xhr: null, fileName: file.name });

      uploadCourseMedia(
        file,
        (pct) => this.updateBulkUpload(sectionId, uploadId, { progress: pct }),
        (xhr) => this.updateBulkUpload(sectionId, uploadId, { xhr })
      )
        .then((url) =>
          this.enqueueBulkFinalize(sectionId, () =>
            apiFetch(`/courses/${this.state.id}/sections/${sectionId}/items`, {
              method: "POST",
              body: {
                title: stripExtension(file.name),
                itemType: detectItemType(file.name),
                contentUrl: url,
                isFreePreview: false,
                downloadAllowed: false,
                sortOrder: baseSortOrder + index,
              },
            }).then(() => this.refreshCourse())
          )
        )
        .catch((error) => {
          if (error instanceof UploadCancelledError) return;
          console.error("Error:", error);
          this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: `فشل رفع ${file.name}: ${error.message || "خطأ في الإتصال"}` });
        })
        .finally(() => this.updateBulkUpload(sectionId, uploadId, null));
    });

    event.options && event.options.clear && event.options.clear();
  };

  cancelBulkUpload = (sectionId, uploadId) => {
    const info = (this.state.bulkUploads[sectionId] || {})[uploadId];
    info && info.xhr && info.xhr.abort();
  };

  deleteItem = (section, item) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا العنصر؟")) return;
    apiFetch(`/courses/${this.state.id}/sections/${section.id}/items/${item.id}`, { method: "DELETE" })
      .then(() => this.refreshCourse())
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  // Same full-list renumbering as moveSection above, and for the same reason -- swapping just the
  // two moved items' existing sortOrder values was a silent no-op whenever they happened to share
  // one already.
  moveItem = (section, item, direction) => {
    const items = section.items || [];
    const index = items.findIndex((i) => i.id === item.id);
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= items.length) return;
    const reordered = [...items];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];
    const bodyFor = (it, sortOrder) => ({
      title: it.title,
      description: it.description,
      itemType: it.itemType,
      contentUrl: it.contentUrl,
      thumb: it.thumb,
      isFreePreview: it.isFreePreview,
      downloadAllowed: !!it.downloadAllowed,
      sortOrder,
    });
    Promise.all(
      reordered.map((it, i) =>
        apiFetch(`/courses/${this.state.id}/sections/${section.id}/items/${it.id}`, { method: "PUT", body: bodyFor(it, i) })
      )
    )
      .then(() => this.refreshCourse())
      .catch((error) => console.error("Error:", error));
  };

  toggleItemFreePreview = (section, item, checked) => {
    apiFetch(`/courses/${this.state.id}/sections/${section.id}/items/${item.id}`, {
      method: "PUT",
      body: {
        title: item.title,
        description: item.description,
        itemType: item.itemType,
        contentUrl: item.contentUrl,
        thumb: item.thumb,
        isFreePreview: checked,
        downloadAllowed: !!item.downloadAllowed,
        sortOrder: item.sortOrder,
      },
    })
      .then(() => this.refreshCourse())
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  toggleItemDownload = (section, item, checked) => {
    apiFetch(`/courses/${this.state.id}/sections/${section.id}/items/${item.id}`, {
      method: "PUT",
      body: {
        title: item.title,
        description: item.description,
        itemType: item.itemType,
        contentUrl: item.contentUrl,
        thumb: item.thumb,
        isFreePreview: item.isFreePreview,
        downloadAllowed: checked,
        sortOrder: item.sortOrder,
      },
    })
      .then(() => this.refreshCourse())
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  startMoveItem = (section, item) => {
    this.setState({ movingItem: { item, sectionId: section.id, targetCourseId: this.state.id, targetSectionId: section.id } });
  };

  cancelMoveItem = () => {
    this.setState({ movingItem: null });
  };

  confirmMoveItem = () => {
    const { movingItem, id } = this.state;
    if (!movingItem || !movingItem.targetSectionId) return;
    if (movingItem.targetCourseId === Number(id) && movingItem.targetSectionId === movingItem.sectionId) {
      // No actual change -- just close the picker.
      this.setState({ movingItem: null });
      return;
    }
    apiFetch(`/courses/${id}/items/${movingItem.item.id}/move`, {
      method: "PUT",
      body: { targetCourseId: movingItem.targetCourseId, targetSectionId: movingItem.targetSectionId },
    })
      .then(() => {
        this.setState({ movingItem: null });
        this.toast.current.show({ severity: "success", summary: "تم بنجاح", detail: "تم نقل العنصر" });
        this.refreshCourse();
      })
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  render() {
    const { course, sections, materials, periods, id, allCourses, movingItem, editingSectionId, editingSectionTitle, uploadingThumbProgress, bulkUploads } = this.state;

    const materialOptions = materials.map((m) => ({ label: m.name, value: m.id }));
    const periodOptions = periods.map((p) => ({ label: p.name, value: p.id }));
    const courseOptions = allCourses.map((c) => ({ label: c.title, value: c.id }));
    const moveTargetCourse = movingItem ? allCourses.find((c) => c.id === movingItem.targetCourseId) : null;
    const moveSectionOptions = moveTargetCourse ? (moveTargetCourse.sections || []).map((s) => ({ label: s.title, value: s.id })) : [];

    return (
      <div className="add-lesson-page">
        <Header pageName={id ? "تعديل الدورة" : "دورة جديدة"} />

        <Toast ref={this.toast}></Toast>

        <div className="content">
          <div className="container">
            <Card title={id ? "بيانات الدورة" : "إدخال دورة جديدة"}>
              <form onSubmit={this.saveCourse} action="#" method="POST">
                <div className="p-grid p-fluid">
                  <div className="p-col-12 p-md-6">
                    <div className="p-inputgroup">
                      <span className="p-float-label">
                        <InputText
                          id="course-title"
                          required
                          value={course.title}
                          onChange={(e) => this.setState({ course: { ...course, title: e.target.value } })}
                        />
                        <label htmlFor="course-title">إسم الدورة</label>
                      </span>
                    </div>
                  </div>
                  <div className="p-col-12 p-md-6">
                    <div className="p-inputgroup">
                      <span className="p-float-label">
                        <Dropdown
                          value={course.materialId}
                          options={materialOptions}
                          showClear
                          onChange={(e) => {
                            this.setState({ course: { ...course, materialId: e.value, periodId: null } });
                            this.loadPeriods(e.value);
                          }}
                          placeholder="إختر المادة"
                        />
                      </span>
                    </div>
                  </div>
                  <div className="p-col-12 p-md-6">
                    <div className="p-inputgroup">
                      <span className="p-float-label">
                        <Dropdown
                          value={course.periodId}
                          options={periodOptions}
                          required
                          onChange={(e) => this.setState({ course: { ...course, periodId: e.value } })}
                          placeholder="الفترة (الإشتراك الذي يفتح هذه الدورة)"
                        />
                      </span>
                    </div>
                  </div>
                  <div className="p-col-12 p-md-12">
                    <div className="p-inputgroup">
                      <span className="p-float-label">
                        <InputTextarea
                          rows={4}
                          value={course.description}
                          onChange={(e) => this.setState({ course: { ...course, description: e.target.value } })}
                        />
                        <label>وصف الدورة</label>
                      </span>
                    </div>
                  </div>
                  <div className="p-col-12 p-md-12 course-show-on-home-field">
                    <Checkbox
                      inputId="showOnHome"
                      checked={!!course.showOnHome}
                      onChange={(e) => this.setState({ course: { ...course, showOnHome: e.checked } })}
                    />
                    <label htmlFor="showOnHome">إظهار هذه الدورة في الصفحة الرئيسية</label>
                  </div>
                  <div className="p-col-12 p-md-12">
                    <label className="course-image-field-label">صورة الغلاف (تظهر في الصفحة الرئيسية وصفحة تفاصيل الدورة)</label>
                    <FileUpload
                      name="courseThumb"
                      customUpload
                      uploadHandler={this.uploadCourseThumb}
                      auto
                      disabled={this.state.uploadingThumb}
                      multiple={false}
                      accept="image/*"
                      maxFileSize={20000000}
                      chooseLabel={this.state.uploadingThumb ? "جاري الرفع..." : "إختيار صورة الغلاف"}
                      uploadLabel="رفع الصورة"
                      cancelLabel="إلغاء"
                      emptyTemplate={<p className="p-m-0">إسحب صورة الغلاف هنا ليبدأ التحميل</p>}
                    />
                    {this.state.uploadingThumb ? (
                      <div className="upload-progress-row">
                        <ProgressBar value={uploadingThumbProgress} style={{ flex: 1, height: "10px" }} />
                        <span>{uploadingThumbProgress}%</span>
                        <Button
                          icon="pi pi-times"
                          className="p-button-rounded p-button-text p-button-danger p-button-sm"
                          title="إلغاء الرفع"
                          onClick={this.cancelThumbUpload}
                        />
                      </div>
                    ) : null}
                    {course.image ? (
                      <div className="uploaded-file-row">
                        <img className="course-thumb-preview" src={course.image} alt="course" />
                        <Button
                          icon="pi pi-times"
                          className="p-button-rounded p-button-text p-button-danger"
                          onClick={() => this.setState({ course: { ...course, image: "" } })}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="p-col-12 p-md-12">
                    <label className="course-image-field-label">صورة صفحة الدورات (تظهر في بطاقة الدورة داخل صفحة الدورات)</label>
                    <FileUpload
                      name="courseListImage"
                      customUpload
                      uploadHandler={this.uploadCourseListImage}
                      auto
                      disabled={this.state.uploadingListImage}
                      multiple={false}
                      accept="image/*"
                      maxFileSize={20000000}
                      chooseLabel={this.state.uploadingListImage ? "جاري الرفع..." : "إختيار صورة صفحة الدورات"}
                      uploadLabel="رفع الصورة"
                      cancelLabel="إلغاء"
                      emptyTemplate={<p className="p-m-0">إسحب صورة صفحة الدورات هنا ليبدأ التحميل</p>}
                    />
                    {this.state.uploadingListImage ? (
                      <div className="upload-progress-row">
                        <ProgressBar value={this.state.uploadingListImageProgress} style={{ flex: 1, height: "10px" }} />
                        <span>{this.state.uploadingListImageProgress}%</span>
                        <Button
                          icon="pi pi-times"
                          className="p-button-rounded p-button-text p-button-danger p-button-sm"
                          title="إلغاء الرفع"
                          onClick={this.cancelListImageUpload}
                        />
                      </div>
                    ) : null}
                    {course.listImage ? (
                      <div className="uploaded-file-row">
                        <img className="course-thumb-preview" src={course.listImage} alt="course list" />
                        <Button
                          icon="pi pi-times"
                          className="p-button-rounded p-button-text p-button-danger"
                          onClick={() => this.setState({ course: { ...course, listImage: "" } })}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="p-col-12 p-md-12">
                    <Button className={`add-button ${this.state.saving ? "loading" : ""}`} label={id ? "حفظ التعديلات" : "حفظ ومتابعة إضافة المحتوى"} />
                  </div>
                </div>
              </form>
            </Card>

            {id ? (
              <Card title="محتوى الدورة (الأقسام والدروس)">
                {sections.map((section, sIndex) => {
                  const form = this.state.itemForms[section.id] || EMPTY_ITEM_FORM;
                  return (
                    <div className="course-section" key={section.id}>
                      <div className="course-section-header">
                        {editingSectionId === section.id ? (
                          <div className="section-title-edit">
                            <InputText
                              value={editingSectionTitle}
                              autoFocus
                              onChange={(e) => this.setState({ editingSectionTitle: e.target.value })}
                              onKeyDown={(e) => e.key === "Enter" && this.saveEditSectionTitle(section)}
                            />
                            <Button icon="pi pi-check" className="p-button-rounded p-button-text p-button-success" onClick={() => this.saveEditSectionTitle(section)} />
                            <Button icon="pi pi-times" className="p-button-rounded p-button-text" onClick={this.cancelEditSection} />
                          </div>
                        ) : (
                          <h4>
                            {section.title}
                            <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm" onClick={() => this.startEditSection(section)} />
                          </h4>
                        )}
                        <div>
                          <Button icon="pi pi-arrow-up" className="p-button-rounded p-button-text" disabled={sIndex === 0} onClick={() => this.moveSection(section, -1)} />
                          <Button icon="pi pi-arrow-down" className="p-button-rounded p-button-text" disabled={sIndex === sections.length - 1} onClick={() => this.moveSection(section, 1)} />
                          <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" onClick={() => this.deleteSection(section.id)} />
                        </div>
                      </div>

                      {(section.items || []).map((item, iIndex) => (
                        <div key={item.id}>
                          <div className="course-item-row">
                            <div className="course-item-info">
                              <i className={`pi ${item.itemType === "VIDEO" ? "pi-video" : item.itemType === "AUDIO" ? "pi-volume-up" : item.itemType === "IMAGE" ? "pi-image" : item.itemType === "MODEL_3D" ? "pi-box" : "pi-file"}`} />
                              <span>{item.title}</span>
                              <label className="item-free-preview-toggle">
                                <Checkbox checked={!!item.isFreePreview} onChange={(e) => this.toggleItemFreePreview(section, item, e.checked)} />
                                {" "}معاينة مجانية
                              </label>
                              <label className="item-free-preview-toggle">
                                <Checkbox checked={!!item.downloadAllowed} onChange={(e) => this.toggleItemDownload(section, item, e.checked)} />
                                {" "}السماح بالتحميل
                              </label>
                            </div>
                            <div>
                              <Button icon="pi pi-arrow-up" className="p-button-rounded p-button-text" disabled={iIndex === 0} onClick={() => this.moveItem(section, item, -1)} />
                              <Button icon="pi pi-arrow-down" className="p-button-rounded p-button-text" disabled={iIndex === (section.items || []).length - 1} onClick={() => this.moveItem(section, item, 1)} />
                              <Button icon="pi pi-directions" className="p-button-rounded p-button-text" title="نقل إلى قسم/دورة أخرى" onClick={() => this.startMoveItem(section, item)} />
                              <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" onClick={() => this.deleteItem(section, item)} />
                            </div>
                          </div>
                          {movingItem && movingItem.item.id === item.id ? (
                            <div className="move-item-form">
                              <Dropdown
                                value={movingItem.targetCourseId}
                                options={courseOptions}
                                onChange={(e) => {
                                  const targetCourse = allCourses.find((c) => c.id === e.value);
                                  const firstSectionId = targetCourse && targetCourse.sections && targetCourse.sections.length ? targetCourse.sections[0].id : null;
                                  this.setState({ movingItem: { ...movingItem, targetCourseId: e.value, targetSectionId: firstSectionId } });
                                }}
                                placeholder="الدورة"
                              />
                              <Dropdown
                                value={movingItem.targetSectionId}
                                options={moveSectionOptions}
                                onChange={(e) => this.setState({ movingItem: { ...movingItem, targetSectionId: e.value } })}
                                placeholder="القسم"
                              />
                              <Button label="نقل" icon="pi pi-check" onClick={this.confirmMoveItem} />
                              <Button label="إلغاء" className="p-button-text" onClick={this.cancelMoveItem} />
                            </div>
                          ) : null}
                        </div>
                      ))}

                      <div className="add-item-form">
                        <InputText
                          placeholder="عنوان العنصر"
                          value={form.title}
                          onChange={(e) => this.updateItemForm(section.id, { title: e.target.value })}
                        />
                        <Dropdown
                          value={form.itemType}
                          options={ITEM_TYPES}
                          onChange={(e) => this.updateItemForm(section.id, { itemType: e.value, contentUrl: "" })}
                          placeholder="نوع العنصر"
                        />
                        <FileUpload
                          name={`item-${section.id}`}
                          customUpload
                          uploadHandler={(e) => this.uploadItemFile(section.id, e)}
                          auto
                          disabled={!!form.uploading}
                          multiple={false}
                          accept={
                            form.itemType === "VIDEO" ? "video/*" :
                            form.itemType === "AUDIO" ? "audio/*" :
                            form.itemType === "IMAGE" ? "image/*" :
                            form.itemType === "MODEL_3D" ? ".3dm" :
                            ".doc,.docx,.pdf,.ppt,.pptx"
                          }
                          maxFileSize={10737418240}
                          chooseLabel={form.uploading ? "جاري الرفع..." : form.contentUrl ? "إستبدال الملف" : "رفع الملف"}
                          uploadLabel="رفع"
                          cancelLabel="إلغاء"
                          mode="basic"
                        />
                        {form.uploading ? (
                          <div className="upload-progress-row">
                            <ProgressBar value={form.uploadProgress || 0} style={{ flex: 1, height: "10px" }} />
                            <span>{form.uploadProgress || 0}%</span>
                            <Button
                              icon="pi pi-times"
                              className="p-button-rounded p-button-text p-button-danger p-button-sm"
                              title="إلغاء الرفع"
                              onClick={() => this.cancelItemUpload(section.id)}
                            />
                          </div>
                        ) : null}
                        {!form.uploading && form.contentUrl ? (
                          <div className="uploaded-file-row">
                            <i className="pi pi-check-circle" style={{ color: "green" }} />
                            <span>{form.fileName || "تم رفع الملف"}</span>
                            <Button
                              icon="pi pi-times"
                              className="p-button-rounded p-button-text p-button-danger"
                              onClick={() => this.removeUploadedItemFile(section.id)}
                            />
                          </div>
                        ) : null}
                        <label>
                          <Checkbox
                            checked={!!form.isFreePreview}
                            onChange={(e) => this.updateItemForm(section.id, { isFreePreview: e.checked })}
                          />
                          {" "}معاينة مجانية
                        </label>
                        <label>
                          <Checkbox
                            checked={!!form.downloadAllowed}
                            onChange={(e) => this.updateItemForm(section.id, { downloadAllowed: e.checked })}
                          />
                          {" "}السماح بالتحميل
                        </label>
                        <Button label="إضافة العنصر" icon="pi pi-plus" onClick={() => this.addItem(section)} />
                      </div>

                      <div className="bulk-upload-form">
                        <FileUpload
                          name={`bulk-${section.id}`}
                          customUpload
                          uploadHandler={(e) => this.uploadBulkFiles(section, e)}
                          auto
                          multiple
                          accept="video/*,audio/*,image/*,.pdf,.doc,.docx,.ppt,.pptx,.3dm"
                          maxFileSize={10737418240}
                          chooseLabel="رفع عدة ملفات دفعة واحدة"
                          uploadLabel="رفع"
                          cancelLabel="إلغاء"
                          mode="basic"
                        />
                        <p className="bulk-upload-hint">
                          كل ملف هيتحول لعنصر مستقل بعنوان اسم الملف، ونوعه هيتحدد تلقائيًا حسب امتداده (فيديو/صوت/صورة/مستند/نموذج 3D).
                        </p>
                        {Object.entries(bulkUploads[section.id] || {}).map(([uploadId, info]) => (
                          <div className="upload-progress-row bulk-upload-progress-row" key={uploadId}>
                            <span className="bulk-upload-filename">{info.fileName}</span>
                            <ProgressBar value={info.progress} style={{ flex: 1, height: "10px" }} />
                            <span>{info.progress}%</span>
                            <Button
                              icon="pi pi-times"
                              className="p-button-rounded p-button-text p-button-danger p-button-sm"
                              title="إلغاء الرفع"
                              onClick={() => this.cancelBulkUpload(section.id, uploadId)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div className="add-item-form">
                  <InputText
                    placeholder="عنوان القسم الجديد"
                    value={this.state.newSectionTitle}
                    onChange={(e) => this.setState({ newSectionTitle: e.target.value })}
                  />
                  <Button label="إضافة قسم" icon="pi pi-plus" onClick={this.addSection} />
                </div>
              </Card>
            ) : (
              <Card>
                <p>احفظ الدورة أولاً لتتمكن من إضافة الأقسام والمحتوى.</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default CourseBuilder;
